'use strict';

var FirebaseStorage = require('./FirebaseStorage');
var KayleeBase = require('./KayleeBase');
//var log = require('../log/Log').channel('KayleeUtil');

var KayleeUtil = {
    bindPrototypeMethods: bindPrototypeMethods,
    clearTimeout: null,
    deepCopy: deepCopy,
    getObjStr: getObjStr,
    getPropertyChain: getPropertyChain,
    getPropertyChainStr: getPropertyChainStr,
    loadActorData: loadActorData,
    loadCallsByRoomIndex: loadCallsByRoomIndex,
    loadCompanyData: loadCompanyData,
    loadControllerData: loadControllerData,
    loadControllersByRoomIndex: loadControllersByRoomIndex,
    loadData: loadData, // not intended for use outside of Kaylee
    loadEquipmentByRoomIndex: loadEquipmentByRoomIndex,
    loadEquipmentData: loadEquipmentData,
    loadEvent: loadEvent,
    loadEventsByCompanyIndex: loadEventsByCompanyIndex,
    loadRoomApiKeyInfoData: loadRoomApiKeyInfoData,
    loadRoomConfigData: loadRoomConfigData,
    loadRoomConfigValidatedData: loadRoomConfigValidatedData,
    loadRoomData: loadRoomData,
    loadRoomsBySiteIndex: loadRoomsBySiteIndex,
    loadSiteData: loadSiteData,
    loadSitesByCompanyIndex: loadSitesByCompanyIndex,
    loadUserData: loadUserData,
    loadUsersByCompanyIndex: loadUsersByCompanyIndex,
    loadUsersByRoomIndex: loadUsersByRoomIndex,
    loadUsersBySiteIndex: loadUsersBySiteIndex,
    objEquals: objEquals,
    setTimeout: null,
    timeout: timeout,
};

// Define a timer functions that work whether we're in a browser environment
// or a node environment.

if (typeof window === 'undefined') {
    var timers = require('timers');
    KayleeUtil.clearTimeout = timers.clearTimeout.bind(timers);
    KayleeUtil.setTimeout = timers.setTimeout.bind(timers);
} else {
    KayleeUtil.clearTimeout = window.clearTimeout.bind(window);
    KayleeUtil.setTimeout = window.setTimeout.bind(window);
}

// Returns a promise that resolves with result after delay milliseconds. It's
// useful when combined with race() for setting time limits on promises.

function timeout(result, delay) {
    return new Promise(function(resolve, reject) {
        KayleeUtil.setTimeout(resolve, delay, result);
    });
}

function bindPrototypeMethods(proto, context, dest) {
    for (var key in proto) {
        if (!proto.hasOwnProperty(key)) continue;
        dest[key] = proto[key].bind(context);
    }
}

// This is a deep copy function meant specifically for sub-properties of
// FirebaseObject instances. It is aware of FirebaseStorage objects and creates
// new instances of them in the deep copy. For a generic deep copy, use
// Sutl.deepCopy().

function deepCopy(orig) {
    if (orig === null) return orig;

    if (orig instanceof Date) return new Date(orig.getTime());

    if (orig instanceof Array) {
        var arrayCopy = [];

        for (var i = 0; i < orig.length; i++) {
            arrayCopy.push(deepCopy(orig[i]));
        }

        return arrayCopy;
    }

    if (orig instanceof FirebaseStorage) {
        return new FirebaseStorage(orig.pathStr, orig.key, orig.isWildCard);
    }

    if (typeof orig === 'object' && orig !== {}) {
        var objCopy = {};

        for (var key in orig) {
            if (!(key in orig)) continue;
            objCopy[key] = deepCopy(orig[key]);
        }

        return objCopy;
    }

    return orig;
}

// Debugging function to print an object

function getObjStr(obj, maxIndent, indent) {
    if (typeof maxIndent === 'undefined') maxIndent = 4;
    if (typeof indent === 'undefined') indent = 1;

    if (indent > maxIndent) return '<indent exceeded>';

    var indentStr = '';
    var prevIndentStr = '';
    var str = '';

    for (var i = 0; i < indent; i++) {
        prevIndentStr = indentStr;
        indentStr += '    ';
    }

    if (typeof obj === 'undefined') {
        str += '[undefined]';
    } else if (obj === null) {
        str += '[null]';
    } else if (typeof obj === 'function') {
        //str += '[' + obj + ']';
        str += '[function]';
    } else if (typeof obj === 'object') {
        var prop;

        if (Array.isArray(obj)) {
            str += '[array {\n';
        } else {
            str += '[object {\n';
        }

        for (prop in obj) {
            if (!obj.hasOwnProperty(prop)) continue;
            str += indentStr + prop + ': ' +
                getObjStr(obj[prop], maxIndent, indent + 1) + '\n';
        }

        var proto = Object.getPrototypeOf(obj);

        if (typeof proto !== 'undefined' && proto !== null) {
            str += indentStr + '[prototype]: ' +
                getObjStr(proto, maxIndent, indent + 1) + '\n';
        }

        str += prevIndentStr + '}]';
    } else {
        str += obj.toString();
    }

    return str;
}

// Debugging function to get the property chain for an object.

function getPropertyChain(obj) {
    if (typeof obj === 'undefined' || obj === null) return null;

    var chain = {};
    chain.own = Object.keys(obj);
    chain.proto = getPropertyChain(Object.getPrototypeOf(obj));
    return chain;
}

function getPropertyChainStr(obj) {
    return JSON.stringify(getPropertyChain(obj), null, 4);
}

// Return a promise that resolves with the data at the specified database path.
// If rejectNull is true, then the promise is rejected if the data is null.
//
// The data is cached for a short amount of time in case someone else needs to
// load the same data.

var dataCacheTimeoutMs = 5000;

function loadData(name, obj, ref, rejectNull) {
    if (typeof rejectNull === 'undefined') rejectNull = true;

    return new Promise(function(resolve, reject) {
        var firstTime = true;
        var cb = ref.on('value', function(snap) {

            if (!firstTime) return;
            firstTime = false;

            KayleeUtil.setTimeout(function() {
                ref.off('value', cb);
            }, dataCacheTimeoutMs);

            if (rejectNull && !snap.exists()) {
                reject(new Error('null data at ' + ref));
                return;
            }
            obj[name] = snap.val();
            resolve(obj);
        }, function(err) {
            reject(err);
        });
    });
}

function loadActorData(obj, rejectNull) {
    if (obj.hasOwnProperty('actor')) {
        return Promise.reject(
            new Error('object already contains actor property'));
    }

    var actorUserId;

    if (obj.hasOwnProperty('actorUserId')) {
        actorUserId = obj.actorUserId;
    } else {
        return Promise.reject(
            new Error('could not get actorUserId from object'));
    }

    return loadData('actor', obj,
        KayleeBase.app.database().ref('users/' + actorUserId), rejectNull);
}

function loadCallsByRoomIndex(obj, rejectNull) {
    if (obj.hasOwnProperty('callsByRoom')) {
        return Promise.reject(
            new Error('object already contains callsByRoom property'));
    }

    var roomId;

    if (obj.hasOwnProperty('roomId')) {
        roomId = obj.roomId;
    } else {
        return Promise.reject(
            new Error('could not get roomId from object'));
    }

    return loadData('callsByRoom', obj,
        KayleeBase.app.database().ref('index/callsByRoom/' + roomId),
        rejectNull);
}

function loadCompanyData(obj, rejectNull) {
    if (obj.hasOwnProperty('company')) {
        return Promise.reject(
            new Error('object already contains company property'));
    }

    var companyId;

    if (obj.hasOwnProperty('companyId')) {
        companyId = obj.companyId;
    } else if (obj.hasOwnProperty('site')) {
        companyId = obj.site.companyId;
    } else if (obj.hasOwnProperty('room')) {
        companyId = obj.room.companyId;
    } else {
        return Promise.reject(
            new Error('could not get companyId from object'));
    }

    return loadData('company', obj,
        KayleeBase.app.database().ref('companies/' + companyId), rejectNull);
}

function loadControllerData(obj, rejectNull) {
    if (obj.hasOwnProperty('controller')) {
        return Promise.reject(
            new Error('object already contains controller property'));
    }

    var controllerId;

    if (obj.hasOwnProperty('controllerId')) {
        controllerId = obj.controllerId;
    } else {
        return Promise.reject(
            new Error('could not get controllerId from object'));
    }

    return loadData('controller', obj,
        KayleeBase.app.database().ref('controllers/' + controllerId), rejectNull);
}

function loadControllersByRoomIndex(obj, rejectNull) {
    if (obj.hasOwnProperty('controllersByRoom')) {
        return Promise.reject(
            new Error('object already contains controllersByRoom property'));
    }

    var roomId;

    if (obj.hasOwnProperty('roomId')) {
        roomId = obj.roomId;
    } else {
        return Promise.reject(
            new Error('could not get roomId from object'));
    }

    return loadData('controllersByRoom', obj,
        KayleeBase.app.database().ref('index/controllersByRoom/' + roomId),
        rejectNull);
}

function loadEquipmentByRoomIndex(obj, rejectNull) {
    if (obj.hasOwnProperty('equipmentByRoom')) {
        return Promise.reject(
            new Error('object already contains equipmentByRoom property'));
    }

    var roomId;

    if (obj.hasOwnProperty('roomId')) {
        roomId = obj.roomId;
    } else {
        return Promise.reject(
            new Error('could not get roomId from object'));
    }

    return loadData('equipmentByRoom', obj,
        KayleeBase.app.database().ref('index/equipmentByRoom/' + roomId),
        rejectNull);
}

function loadEquipmentData(obj, rejectNull) {
    if (obj.hasOwnProperty('equipment')) {
        return Promise.reject(
            new Error('object already contains equipment property'));
    }

    var equipmentId;

    if (obj.hasOwnProperty('equipmentId')) {
        equipmentId = obj.equipmentId;
    } else {
        return Promise.reject(
            new Error('could not get equipmentId from object'));
    }

    return loadData('equipment', obj,
        KayleeBase.app.database().ref('equipment/' + equipmentId), rejectNull);
}

function loadEvent(obj, rejectNull) {
    if (obj.hasOwnProperty('event')) {
        return Promise.reject(
            new Error('object already contains event property'));
    }

    var eventId;

    if (obj.hasOwnProperty('eventId')) {
        eventId = obj.eventId;
    } else {
        return Promise.reject(
            new Error('could not get eventId from object'));
    }

    return loadData('event', obj,
        KayleeBase.app.database().ref('events/' + eventId), rejectNull);
}

function loadEventsByCompanyIndex(obj, rejectNull) {
    if (obj.hasOwnProperty('eventsByCompany')) {
        return Promise.reject(
            new Error('object already contains eventsByCompany property'));
    }

    var companyId;

    if (obj.hasOwnProperty('companyId')) {
        companyId = obj.companyId;
    } else {
        return Promise.reject(
            new Error('could not get companyId from object'));
    }

    return loadData('eventsByCompany', obj,
        KayleeBase.app.database().ref('index/eventsByCompany/' + companyId),
        rejectNull);
}

function loadRoomApiKeyInfoData(obj, rejectNull) {
    if (obj.hasOwnProperty('roomApiKeyInfo')) {
        return Promise.reject(
            new Error('object already contains roomApiKeyInfo property'));
    }

    var roomId;
    var apiKey;

    if (obj.hasOwnProperty('roomId')) {
        roomId = obj.roomId;
    } else {
        return Promise.reject(
            new Error('could not get roomId from object'));
    }

    if (obj.hasOwnProperty('apiKey')) {
        apiKey = obj.apiKey;
    } else {
        return Promise.reject(
            new Error('could not get apiKey from object'));
    }

    return loadData('roomApiKeyInfo', obj,
        KayleeBase.app.database().ref('roomApiKeys/' + roomId + '/' + apiKey), rejectNull);
}

function loadRoomConfigData(obj, rejectNull) {
    if (obj.hasOwnProperty('roomConfig')) {
        return Promise.reject(
            new Error('object already contains roomConfig property'));
    }

    var roomId;

    if (obj.hasOwnProperty('roomId')) {
        roomId = obj.roomId;
    } else {
        return Promise.reject(
            new Error('could not get roomId from object'));
    }

    return loadData('roomConfig', obj,
        KayleeBase.app.database().ref('roomConfig/' + roomId), rejectNull);
}

function loadRoomConfigValidatedData(obj, rejectNull) {
    if (obj.hasOwnProperty('roomConfigValidated')) {
        return Promise.reject(
            new Error('object already contains roomConfigValidated property'));
    }

    var roomId;

    if (obj.hasOwnProperty('roomId')) {
        roomId = obj.roomId;
    } else {
        return Promise.reject(
            new Error('could not get roomId from object'));
    }

    return loadData('roomConfigValidated', obj,
        KayleeBase.app.database().ref('roomConfigValidated/' + roomId), rejectNull);
}

function loadRoomData(obj, rejectNull) {
    if (obj.hasOwnProperty('room')) {
        return Promise.reject(
            new Error('object already contains room property'));
    }

    var roomId;

    if (obj.hasOwnProperty('roomId')) {
        roomId = obj.roomId;
    } else {
        return Promise.reject(
            new Error('could not get roomId from object'));
    }

    return loadData('room', obj,
        KayleeBase.app.database().ref('rooms/' + roomId), rejectNull);
}

function loadRoomsBySiteIndex(obj, rejectNull) {
    if (obj.hasOwnProperty('roomsBySite')) {
        return Promise.reject(
            new Error('object already contains roomsBySite property'));
    }

    var siteId;

    if (obj.hasOwnProperty('siteId')) {
        siteId = obj.siteId;
    } else if (obj.hasOwnProperty('room')) {
        siteId = obj.room.siteId;
    } else {
        return Promise.reject(
            new Error('could not get siteId from object'));
    }

    return loadData('roomsBySite', obj,
        KayleeBase.app.database().ref('index/roomsBySite/' + siteId),
        rejectNull);
}

function loadSiteData(obj, rejectNull) {
    if (obj.hasOwnProperty('site')) {
        return Promise.reject(
            new Error('object already contains site property'));
    }

    var siteId;

    if (obj.hasOwnProperty('siteId')) {
        siteId = obj.siteId;
    } else if (obj.hasOwnProperty('room')) {
        siteId = obj.room.siteId;
    } else {
        return Promise.reject(
            new Error('could not get siteId from object'));
    }

    return loadData('site', obj,
        KayleeBase.app.database().ref('sites/' + siteId), rejectNull);
}

function loadSitesByCompanyIndex(obj, rejectNull) {
    if (obj.hasOwnProperty('sitesByCompany')) {
        return Promise.reject(
            new Error('object already contains sitesByCompany property'));
    }

    var companyId;

    if (obj.hasOwnProperty('companyId')) {
        companyId = obj.companyId;
    } else if (obj.hasOwnProperty('site')) {
        companyId = obj.site.companyId;
    } else if (obj.hasOwnProperty('room')) {
        companyId = obj.room.companyId;
    } else {
        return Promise.reject(
            new Error('could not get companyId from object'));
    }

    return loadData('sitesByCompany', obj,
        KayleeBase.app.database().ref('index/sitesByCompany/' + companyId),
        rejectNull);
}

function loadUserData(obj, rejectNull) {
    if (obj.hasOwnProperty('user')) {
        return Promise.reject(
            new Error('object already contains user property'));
    }

    var userId;

    if (obj.hasOwnProperty('userId')) {
        userId = obj.userId;
    } else {
        return Promise.reject(
            new Error('could not get userId from object'));
    }

    return loadData('user', obj,
        KayleeBase.app.database().ref('users/' + userId), rejectNull);
}

function loadUsersByCompanyIndex(obj, rejectNull) {
    if (obj.hasOwnProperty('usersByCompany')) {
        return Promise.reject(
            new Error('object already contains usersByCompany property'));
    }

    var companyId;

    if (obj.hasOwnProperty('companyId')) {
        companyId = obj.companyId;
    } else if (obj.hasOwnProperty('site')) {
        companyId = obj.site.companyId;
    } else if (obj.hasOwnProperty('room')) {
        companyId = obj.room.companyId;
    } else {
        return Promise.reject(
            new Error('could not get companyId from object'));
    }

    return loadData('usersByCompany', obj,
        KayleeBase.app.database().ref('index/usersByCompany/' + companyId),
        rejectNull);
}

function loadUsersByRoomIndex(obj, rejectNull) {
    if (obj.hasOwnProperty('usersByRoom')) {
        return Promise.reject(
            new Error('object already contains usersByRoom property'));
    }

    var roomId;

    if (obj.hasOwnProperty('roomId')) {
        roomId = obj.roomId;
    } else {
        return Promise.reject(
            new Error('could not get roomId from object'));
    }

    return loadData('usersByRoom', obj,
        KayleeBase.app.database().ref('index/usersByRoom/' + roomId),
        rejectNull);
}

function loadUsersBySiteIndex(obj, rejectNull) {
    if (obj.hasOwnProperty('usersBySite')) {
        return Promise.reject(
            new Error('object already contains usersBySite property'));
    }

    var siteId;

    if (obj.hasOwnProperty('siteId')) {
        siteId = obj.siteId;
    } else if (obj.hasOwnProperty('room')) {
        siteId = obj.room.siteId;
    } else {
        return Promise.reject(
            new Error('could not get siteId from object'));
    }

    return loadData('usersBySite', obj,
        KayleeBase.app.database().ref('index/usersBySite/' + siteId),
        rejectNull);
}

// This is a FirebaseObject-aware object comparison function for comparing
// sub-properties of FirebaseObjects. It is aware of FirebaseStorage objects
// and compares them by their internal key. For a generic object compare
// function, use Sutl.objEquals().

function objEquals(a, b) {
    if (a === undefined && b === undefined) return true;
    if (a === null && b === null) return true;
    if (a === undefined || b === undefined || a === null || b === null) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;

        for (var i = 0; i < a.length; i++) {
            if (!objEquals(a[i], b[i])) return false;
        }

        return true;
    }

    if (typeof a !== 'object' || typeof b !== 'object') return a === b;

    var aKeys = Object.keys(a);
    var bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) return false;

    for (var aKey in a) {
        if (!(aKey in a)) continue;
        if (!(aKey in b)) return false;

        if (typeof a[aKey] !== typeof b[aKey]) return false;

        if (typeof a[aKey] === 'object') {
            if (!objEquals(a[aKey], b[aKey])) return false;
        } else if (a[aKey] instanceof FirebaseStorage &&
            b[aKey] instanceof FirebaseStorage) {
            return a.key === b.key;
        } else {
            if (a[aKey] !== b[aKey]) return false;
        }
    }

    return true;
}

module.exports = KayleeUtil;
